import { css } from 'aphrodite';
import { format, isSameDay } from 'date-fns';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { ClockAlertIcon } from '../../../components/svgs/icons/ClockAlertIcon';
import { styleSheet } from '../styles';
import { CalendarItem } from '../types';
import { getDayKey } from '../utils/dateRange';
import { formatTime } from '../utils/format';
import { MINUTES_IN_DAY, earliestStartMinute, groupItemsByDay, layoutDayItems } from '../utils/layout';

/** Max all-day chips shown per day in the multi-day (week) view before collapsing to "+N more". */
const MAX_ALL_DAY_CHIPS = 3;
/** Pixel height of one hour row. Total grid height is 24 × this. */
const HOUR_HEIGHT = 48;
/** Minimum block height — enough for a single (compact) line on very short events. */
const MIN_BLOCK_HEIGHT = 18;
/** At/above this height a block has room for the two-line (time over title) layout. */
const TWO_LINE_MIN_HEIGHT = 38;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const GRID_HEIGHT = (MINUTES_IN_DAY / 60) * HOUR_HEIGHT;

const minutesToPx = (minutes: number): number => (minutes / 60) * HOUR_HEIGHT;

interface IProps {
	days: Date[];
	items: CalendarItem[];
	onSelectItem: (item: CalendarItem) => void;
	/** Drill into a single day (used by the week view's all-day "+N more"). */
	onSelectDate?: (date: Date) => void;
	/**
	 * More undated action items exist on the server than were fetched onto today. Renders today's
	 * all-day overflow as "N+ more" so the roll-up doesn't imply it holds every undated item.
	 */
	undatedHasMore?: boolean;
}

/** Google-Calendar-style time grid powering both the week (7 columns) and day (1 column) views. */
export const TimeGridView: React.FC<IProps> = ({ days, items, onSelectItem, onSelectDate, undatedHasMore = false }) => {
	// The all-day band always caps per day so a heavy day can't crowd out the hourly grid. In the
	// week view "+N more" drills into the day; in the (single-day) day view there's nowhere deeper
	// to go, so it expands the band inline.
	const isDayView = days.length === 1;
	const [allDayExpanded, setAllDayExpanded] = React.useState(false);
	const scrollRef = React.useRef<HTMLDivElement>(null);
	const itemsByDay = React.useMemo(() => groupItemsByDay(items), [items]);

	// All-day items (e.g. action items — due date only) render in a top band, not the hourly
	// grid; only timed items are positioned by time.
	const positionedByDay = React.useMemo(
		() =>
			days.map(day =>
				layoutDayItems(
					(itemsByDay.get(getDayKey(day)) ?? []).filter(item => !item.allDay),
					day
				)
			),
		[days, itemsByDay]
	);
	const allDayByDay = React.useMemo(
		() =>
			days.map(day =>
				(itemsByDay.get(getDayKey(day)) ?? [])
					.filter(item => !!item.allDay)
					// Undated items sort after dated ones so genuine due work shows first and undated
					// overflow collapses into "+N more".
					.sort((a, b) => Number(!!a.undated) - Number(!!b.undated) || a.start.getTime() - b.start.getTime())
			),
		[days, itemsByDay]
	);

	// Scroll to the earliest item once per window, and only once that window's data is actually
	// placed. Keying on the window (not the data) keeps background refetches from resetting the
	// user's scroll; waiting for real data avoids a double jump when keepPreviousData briefly shows
	// the prior window's (non-matching) items before the new fetch resolves.
	const windowKey = days.length ? `${getDayKey(days[0])}_${getDayKey(days[days.length - 1])}` : '';
	const scrolledWindowRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		if (scrolledWindowRef.current === windowKey) {
			return;
		}
		const earliest = positionedByDay.reduce<number | null>((min, dayPositions) => {
			const dayMin = earliestStartMinute(dayPositions);
			if (dayMin === null) {
				return min;
			}
			return min === null ? dayMin : Math.min(min, dayMin);
		}, null);
		if (earliest === null) {
			return;
		}
		scrolledWindowRef.current = windowKey;
		if (scrollRef.current) {
			scrollRef.current.scrollTop = Math.max(0, minutesToPx(earliest) - HOUR_HEIGHT);
		}
	}, [windowKey, positionedByDay]);

	// Advance the now-line on a minute cadence instead of freezing at mount.
	const [now, setNow] = React.useState(() => new Date());
	React.useEffect(() => {
		const id = setInterval(() => setNow(new Date()), 60_000);
		return () => clearInterval(id);
	}, []);
	const nowMinutes = now.getHours() * 60 + now.getMinutes();

	return (
		<div className={css(styleSheet.gridWrap)}>
			<div className={css(styleSheet.gridHeaderRow)}>
				<div className={css(styleSheet.gridHeaderGutter)} />
				{days.map(day => {
					const isToday = isSameDay(day, now);
					return (
						<div key={getDayKey(day)} className={css(styleSheet.gridHeaderDay)}>
							<span className={css(styleSheet.gridHeaderDayName, isToday && styleSheet.gridHeaderToday)}>
								{format(day, 'EEE')}
							</span>
							<span className={css(styleSheet.gridHeaderDayNumber, isToday && styleSheet.gridHeaderDayNumberToday)}>
								{format(day, 'd')}
							</span>
						</div>
					);
				})}
			</div>

			{/* Always render the all-day band so navigating weeks with/without all-day items
			    doesn't make the grid jump. */}
			<div className={css(styleSheet.allDayRow, isDayView && allDayExpanded && styleSheet.allDayRowExpanded)}>
				<div className={css(styleSheet.allDayGutter)}>All day</div>
				{days.map((day, dayIndex) => {
					const dayAllDay = allDayByDay[dayIndex];
					const expanded = isDayView && allDayExpanded;
					const visible = expanded ? dayAllDay : dayAllDay.slice(0, MAX_ALL_DAY_CHIPS);
					const overflow = dayAllDay.length - visible.length;
					const isToday = isSameDay(day, now);
					return (
						<div key={getDayKey(day)} className={css(styleSheet.allDayCell)}>
							{visible.map(item => (
								<Button
									key={`${item.kind}-${item.id}`}
									variant='ghost'
									styles={[
										styleSheet.resetButton,
										styleSheet.monthChip,
										styleSheet.allDayChip,
										item.faded && styleSheet.faded,
									]}
									title={item.title}
									onClick={() => onSelectItem(item)}
									style={{
										borderLeftColor: item.color,
										backgroundColor: `color-mix(in srgb, ${item.color} 14%, var(--color-background-light))`,
									}}
								>
									{item.overdue ? (
										<span className={css(styleSheet.overdueIcon)} title='Overdue'>
											<ClockAlertIcon />
										</span>
									) : null}
									<span className={css(styleSheet.meetingBlockTitleInline, item.muted && styleSheet.cancelledText)}>
										{item.title}
									</span>
								</Button>
							))}
							{overflow > 0 ? (
								<Button
									variant='ghost'
									styles={[styleSheet.resetButton, styleSheet.monthMore, styleSheet.allDayMore]}
									onClick={() => (isDayView ? setAllDayExpanded(true) : onSelectDate?.(day))}
								>
									+{overflow}
									{isToday && undatedHasMore ? '+' : ''} more
								</Button>
							) : expanded && dayAllDay.length > MAX_ALL_DAY_CHIPS ? (
								<Button
									variant='ghost'
									styles={[
										styleSheet.resetButton,
										styleSheet.monthMore,
										styleSheet.allDayMore,
										styleSheet.allDayCollapse,
									]}
									onClick={() => setAllDayExpanded(false)}
								>
									Show less
								</Button>
							) : null}
						</div>
					);
				})}
			</div>

			<div className={css(styleSheet.gridScroll)} ref={scrollRef}>
				<div className={css(styleSheet.gridInner)} style={{ height: GRID_HEIGHT }}>
					<div className={css(styleSheet.hoursAxis)}>
						{HOURS.map(hour => (
							<span key={hour} className={css(styleSheet.hourLabel)} style={{ top: hour * HOUR_HEIGHT }}>
								{hour === 0 ? '' : format(new Date(2000, 0, 1, hour), 'h a')}
							</span>
						))}
					</div>

					<div className={css(styleSheet.daysContainer)}>
						{HOURS.map(hour => (
							<div key={`line-${hour}`} className={css(styleSheet.hourLine)} style={{ top: hour * HOUR_HEIGHT }} />
						))}

						{days.map((day, dayIndex) => {
							const isToday = isSameDay(day, now);
							return (
								<div key={getDayKey(day)} className={css(styleSheet.dayColumn)}>
									{isToday ? (
										<div className={css(styleSheet.nowLine)} style={{ top: minutesToPx(nowMinutes) }} />
									) : null}
									{positionedByDay[dayIndex].map(({ item, position }) => {
										const widthPct = 100 / position.columnCount;
										const height = Math.max(MIN_BLOCK_HEIGHT, minutesToPx(position.endMinutes - position.startMinutes));
										// Short events render time + title on one line so they fit their true height.
										const compact = height < TWO_LINE_MIN_HEIGHT;
										return (
											<Button
												key={`${item.kind}-${item.id}`}
												variant='ghost'
												styles={[styleSheet.resetButton, styleSheet.meetingBlock, item.faded && styleSheet.faded]}
												title={item.title}
												onClick={() => onSelectItem(item)}
												style={{
													top: minutesToPx(position.startMinutes),
													height,
													left: `calc(${position.column * widthPct}% + 2px)`,
													width: `calc(${widthPct}% - 4px)`,
													borderLeftColor: item.color,
													backgroundColor: `color-mix(in srgb, ${item.color} 14%, var(--color-background-light))`,
												}}
											>
												{compact ? (
													<span className={css(styleSheet.meetingBlockCompact, item.muted && styleSheet.cancelledText)}>
														<span className={css(styleSheet.meetingBlockTimeInline)}>{formatTime(item.start)}</span>
														<span className={css(styleSheet.meetingBlockTitleInline)}>{item.title}</span>
													</span>
												) : (
													<>
														<span className={css(styleSheet.meetingBlockTime)}>{formatTime(item.start)}</span>
														<span className={css(styleSheet.meetingBlockTitle, item.muted && styleSheet.cancelledText)}>
															{item.title}
														</span>
													</>
												)}
											</Button>
										);
									})}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};
